import { Link } from "react-router-dom";
import { ShieldCheck, Lock, FileText } from "lucide-react";
import { CONTACT_INFO } from "@/data/content";
import usePageTitle from "@/hooks/usePageTitle";

export default function Privacy() {
  usePageTitle("Privacy Policy · Maqam Community Initiatives");
  return (
    <div data-testid="page-privacy" className="bg-white">
      <section className="relative pt-32 md:pt-40 pb-8 md:pb-12 bg-white">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <span className="overline">Privacy Policy</span>
          <h1 className="font-serif text-5xl md:text-6xl text-[color:var(--maqam-navy)] mt-3 leading-[1.05] tracking-tight">
            How we handle your information.
          </h1>
          <p className="mt-6 text-[var(--maqam-muted)] max-w-2xl leading-relaxed">
            Maqam Community Initiatives takes traveller privacy seriously. This policy explains what we collect,
            why, who may receive it, how it is protected, how long it is kept, and your rights.
          </p>
          <p className="mt-3 text-xs uppercase tracking-[0.18em] text-[color:var(--maqam-gold-2)]">
            Last updated: December 2025
          </p>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-4xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
          <div className="review-shadow-hover rounded-2xl border border-[var(--maqam-border)] p-6">
            <ShieldCheck size={22} className="text-[color:var(--maqam-gold)]" />
            <h3 className="mt-3 font-serif text-lg text-[color:var(--maqam-navy)]">Only what's needed</h3>
            <p className="mt-2 text-sm text-[var(--maqam-muted)]">We ask for the minimum information required to serve you well.</p>
          </div>
          <div className="review-shadow-hover rounded-2xl border border-[var(--maqam-border)] p-6">
            <Lock size={22} className="text-[color:var(--maqam-gold)]" />
            <h3 className="mt-3 font-serif text-lg text-[color:var(--maqam-navy)]">Restricted access</h3>
            <p className="mt-2 text-sm text-[var(--maqam-muted)]">Sensitive traveller data is visible only to authorized Maqam staff and the licensed travel provider.</p>
          </div>
          <div className="review-shadow-hover rounded-2xl border border-[var(--maqam-border)] p-6">
            <FileText size={22} className="text-[color:var(--maqam-gold)]" />
            <h3 className="mt-3 font-serif text-lg text-[color:var(--maqam-navy)]">Your rights</h3>
            <p className="mt-2 text-sm text-[var(--maqam-muted)]">You can request access, correction, or deletion at any time.</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-5 md:px-8 mt-14 space-y-10 text-[var(--maqam-ink)] leading-relaxed">
          <Section title="1. What information we collect" testid="privacy-collect">
            <p>
              We collect information in two distinct places, kept separate on purpose:
            </p>
            <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-[color:var(--maqam-gold)]">
              <li>
                <strong className="text-[color:var(--maqam-navy)]">Interest form (this website):</strong> full name, email
                address, phone number, city and country, number of travellers, trip of interest, preferred room type, and any optional message.
              </li>
              <li>
                <strong className="text-[color:var(--maqam-navy)]">Secure registration form (external Google Form):</strong> full
                traveller details required to book, coordinate travel, and comply with airline, hotel, and visa requirements. This
                may include passport information, date of birth, nationality, emergency contact, dietary needs, and relevant
                medical or accessibility information. This data is <em>never</em> stored in our website database.
              </li>
            </ul>
          </Section>

          <Section title="2. Why we collect it" testid="privacy-why">
            <p>Traveller information is collected for:</p>
            <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-[color:var(--maqam-gold)]">
              <li>Registration and coordination of your Umrah journey</li>
              <li>Accommodation, transportation, and airline bookings</li>
              <li>Visa processing where applicable</li>
              <li>Traveller safety and emergency contact</li>
              <li>Related trip administration (group communications, itinerary updates, tour logistics)</li>
            </ul>
          </Section>

          <Section title="3. Who may receive it" testid="privacy-share">
            <p>
              Your information may be shared only with parties directly involved in delivering your trip:
            </p>
            <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-[color:var(--maqam-gold)]">
              <li>Authorized Maqam Community Initiatives staff</li>
              <li>
                Our licensed travel provider — <strong className="text-[color:var(--maqam-navy)]">Amax Travel Canada, TICO
                Registration #50027257</strong> — which handles all bookings, flights, accommodations, visas, transportation, and
                package fulfillment
              </li>
              <li>Airlines, hotels, transport providers, and visa authorities strictly as required to fulfil your trip</li>
              <li>Legal or regulatory authorities when required by law</li>
            </ul>
          </Section>

          <Section title="4. How your information is protected" testid="privacy-protect">
            <p>
              Interest-form submissions on this website travel over HTTPS and are stored in access-restricted systems.
              Full traveller registrations are submitted through Google Forms and delivered to a Google Sheet accessible only to
              designated Maqam staff members. Access is limited to those who need it to serve you.
            </p>
          </Section>

          <Section title="5. How long we keep your information" testid="privacy-retention">
            <p>
              We retain your information only as long as needed to serve you and to meet legal or regulatory obligations of our
              licensed travel provider. Interest-form entries are retained for up to twenty-four months after your last contact
              with us. Full traveller registration data is retained per the requirements of Amax Travel Canada, after which it is
              deleted or anonymized.
            </p>
          </Section>

          <Section title="6. Your rights" testid="privacy-rights">
            <p>
              You may request at any time to:
            </p>
            <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-[color:var(--maqam-gold)]">
              <li>Access the information we hold about you</li>
              <li>Correct any information that is inaccurate</li>
              <li>Delete your information, subject to the retention requirements above</li>
            </ul>
            <p className="mt-4">
              To make a request, email{" "}
              <a
                data-testid="privacy-contact-email"
                href={`mailto:${CONTACT_INFO.email}`}
                className="underline underline-offset-2 decoration-[color:var(--maqam-gold)] text-[color:var(--maqam-navy)] font-medium"
              >
                {CONTACT_INFO.email}
              </a>
              . We respond within a reasonable timeframe and, where the request affects travel-related records, may coordinate
              with our licensed travel provider.
            </p>
          </Section>

          <Section title="7. Compliance" testid="privacy-compliance">
            <p>
              Maqam Community Initiatives facilitates community organization, education, group coordination, and traveller support.
              All travel bookings, travel services, flights, accommodations, visas, transportation, and package fulfillment are
              provided and managed by <strong className="text-[color:var(--maqam-navy)]">Amax Travel Canada, TICO Registration #50027257</strong>.
              Applicable Canadian privacy law (PIPEDA) applies to how we handle personal information.
            </p>
          </Section>

          <Section title="8. Contact" testid="privacy-contact">
            <p>
              Questions about this policy? Reach out at{" "}
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="underline underline-offset-2 decoration-[color:var(--maqam-gold)] text-[color:var(--maqam-navy)] font-medium"
              >
                {CONTACT_INFO.email}
              </a>
              . For general enquiries, visit our{" "}
              <Link to="/contact" className="underline underline-offset-2 decoration-[color:var(--maqam-gold)] text-[color:var(--maqam-navy)] font-medium">
                Contact page
              </Link>.
            </p>
          </Section>
        </div>
      </section>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <h2 className="font-serif text-2xl md:text-3xl text-[color:var(--maqam-navy)] tracking-tight">
        {title}
      </h2>
      <div className="gold-divider mt-3 mb-5" />
      <div className="text-[15px] text-[var(--maqam-muted)] space-y-3">{children}</div>
    </div>
  );
}
